import { Add } from "@mui/icons-material";
import { Box } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Button, DataTable, Input } from "../../components";
import { AppRoutes } from "../../constants";
import useApp from "../../hooks/useApp";
import useAuth from "../../hooks/useAuth";
import { useCategories } from "../../hooks/useCategories";
import { useCities } from "../../hooks/useCities";
import useDebounce from "../../hooks/useDebounce";
import {
  getAllNGO_V2,
  getNGOByOrganization_V2,
} from "../../Services_V2/NGO.service";
import { INGO_V2 } from "../../types";
import { showError } from "../../utils/error";
import { orgNgoColumns } from "./column";
import LinkNgoPopup from "./components/LinkNgoModel/LinkNgoPopup";
import PreviewImage from "./components/PreviewImage";
import styles from "./ngosTablePage.module.css";
import { mobileText } from "../../utils/responsiveStyle";

interface NGOsTablePageProps {
  mode: "admin" | "organization";
}

const NGOsTablePage: React.FC<NGOsTablePageProps> = ({ mode }) => {
  const { dispatch } = useApp();
  const {
    state: { user, userOrganization },
  } = useAuth();
  const isOrgMode = mode === "organization";
  const navigate = useNavigate();
  const { map: categoryMap } = useCategories();
  const { map: cityMap } = useCities();

  const [ngos, setNgos] = useState<INGO_V2[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState<string>("");
  const [currentImageTitle, setCurrentImageTitle] = useState<string>("");
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [linkNgoModalOpen, setLinkNgoModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const showLoader = useCallback(
    (show: boolean) => {
      dispatch({ type: "updateState", payload: { pageLoader: show } });
    },
    [dispatch],
  );

  const getPayload = useCallback(
    () => ({
      page: currentPage + 1,
      limit: rowsPerPage,
      search: debouncedSearchTerm,
    }),
    [currentPage, rowsPerPage, debouncedSearchTerm],
  );

  const openImageModal = (url: string, title: string) => {
    setCurrentImageUrl(url);
    setCurrentImageTitle(title);
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
    setCurrentImageUrl("");
    setCurrentImageTitle("");
  };

  const fetchNGOs = useCallback(async () => {
    showLoader(true);
    setLoading(true);
    try {
      const payload = getPayload();
      const res = isOrgMode
        ? await getNGOByOrganization_V2({
            ...payload,
            organizationId: String(user?.entityId),
          })
        : await getAllNGO_V2(payload);

      setNgos(res?.data ?? []);
      setTotal(res?.total ?? 0);
      setLoading(false);
    } catch (error) {
      showError(error);
    } finally {
      showLoader(false);
      setLoading(false);
    }
  }, [getPayload, showLoader, isOrgMode]);

  // const refetch = useCallback(() => {
  //   setCurrentPage(0);
  //   fetchNGOs();
  // }, [fetchNGOs]);

  useEffect(() => {
    fetchNGOs();
  }, [currentPage, rowsPerPage, debouncedSearchTerm]);

  const handleAddNGO = useCallback(() => {
    if (isOrgMode) {
      navigate(AppRoutes.ORGANIZATION.NGO.ADD);
    } else {
      navigate(AppRoutes.ADMIN.NGO.ADD);
    }
  }, [navigate, isOrgMode]);

  const handleLinkNGO = useCallback(() => {
    setLinkNgoModalOpen(true);
  }, []);

  const handleLinkSuccess = () => {
    fetchNGOs();
  };

  const currentNgoIds = useMemo(() => {
    return ngos.map((ngo) => ngo._id!).filter(Boolean);
  }, [ngos]);

  const handleEdit = useCallback(
    (ngo: INGO_V2) => {
      if (isOrgMode) {
        navigate(AppRoutes.ORGANIZATION.NGO.UPDATE(ngo._id!));
      } else {
        navigate(AppRoutes.ADMIN.NGO.UPDATE(ngo._id!));
      }
    },
    [navigate, isOrgMode],
  );

  const columns = useMemo(() => {
    return orgNgoColumns(categoryMap, cityMap, openImageModal, navigate);
  }, [categoryMap, cityMap, openImageModal, navigate]);

  return (
    <>
      <Box className={styles.container}>
        <Box className={styles.header}>
          <p style={{ fontWeight: 500 }}>
            Total {total} NGOs {isOrgMode ? "in your organization" : "added"}
          </p>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Button variant="outlined" onClick={handleLinkNGO}>
              Link NGO
            </Button>
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {/* <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddNGO}
            >
              Add New NGO
            </Button> */}
          </Box>
        </Box>

        <Box sx={{ mt: 4 }}>
          <DataTable
            data={ngos}
            columns={columns}
            total={total}
            page={currentPage}
            rowsPerPage={rowsPerPage}
            showPagination
            onPageChange={(_, page) => setCurrentPage(page)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(0);
            }}
            onEdit={handleEdit}
            tableCellSx={{
              py: 0.3,
              ...mobileText,
              lineHeight: 1.2,
            }}
            loading={loading}
          />
        </Box>
      </Box>

      <PreviewImage
        open={imageModalOpen}
        onClose={closeImageModal}
        imageUrl={currentImageUrl}
        title={currentImageTitle}
      />
      <LinkNgoPopup
        open={linkNgoModalOpen}
        onClose={() => setLinkNgoModalOpen(false)}
        onLinkSuccess={handleLinkSuccess}
        currentNgoIds={currentNgoIds}
        onAddNgo={handleAddNGO}
      />
    </>
  );
};

export default NGOsTablePage;
